import { AlertTriangle, Info } from 'lucide-react';
import { Inline } from '../../../../design-system/inline/inline';
import { Stack } from '../../../../design-system/stack/stack';
import { cn } from '../../../../utils/cn';
import { exhaustiveCheck } from '../../../../utils/exhaustive-check';

type CardVariant =
  | 'DETAILS_MISMATCH'
  | 'FIRST_TIME_RECIPIENT'
  | 'EXTRACTION_FAILED'
  | 'DOCUMENT_UNREADABLE'
  | 'ALREADY_PAID'
  | 'VENDOR_COUNTRY_NOT_SUPPORTED'
  | 'CURRENCY_NOT_SUPPORTED'
  | 'GENERIC_ERROR';

type AlertLevel = 'INFO' | 'WARNING' | 'ERROR';

type InvoiceWarningCardProps = {
  variant: CardVariant;
  unsupportedVendorCountry?: string;
  unsupportedCurrency?: string;
};

export const InvoiceWarningCard = ({
  variant,
  unsupportedVendorCountry,
  unsupportedCurrency,
}: InvoiceWarningCardProps) => {
  const { alertLevel, title, description } = getCardDetails(variant, unsupportedVendorCountry, unsupportedCurrency);

  const alertIcon = getAlertIcon(alertLevel);

  const isErrorAlert = alertLevel === 'ERROR';

  return (
    <Inline
      space={3}
      wrap="nowrap"
      align="start"
      className="rounded-lg border-border bg-white px-4 py-3 md:items-center"
    >
      <div
        className={cn('h-10 w-10 shrink-0 rounded-full p-2.5', {
          'bg-primary/20': !isErrorAlert,
          'bg-destructive/20': isErrorAlert,
        })}
      >
        {alertIcon}
      </div>

      <Stack space={1}>
        {title && <h3 className="text-base font-semibold text-destructive">{title}</h3>}

        <p
          className={cn('font-medium', {
            'text-base text-black': !isErrorAlert,
            'text-sm text-muted-foreground': isErrorAlert,
          })}
        >
          {description}
        </p>
      </Stack>
    </Inline>
  );
};

const getCardDetails = (
  variant: CardVariant,
  unsupportedVendorCountry?: string,
  unsupportedCurrency?: string,
): { alertLevel: AlertLevel; title?: string; description: string } => {
  switch (variant) {
    case 'DETAILS_MISMATCH':
      return {
        alertLevel: 'WARNING',
        description: `The bank details on this invoice don't match the ones you've used for this recipient before. Please check before you continue`,
      };
    case 'FIRST_TIME_RECIPIENT':
      return {
        alertLevel: 'INFO',
        description: 'This invoice is from a new recipient. Please double-check their details below before continuing.',
      };
    case 'EXTRACTION_FAILED':
      return {
        alertLevel: 'INFO',
        description: `We couldn't read the details on this invoice. Please select an existing recipient or add a new one to continue.`,
      };
    case 'DOCUMENT_UNREADABLE':
      return {
        alertLevel: 'INFO',
        description: `We couldn't read the details on this invoice. Please select an existing recipient or add a new one to continue.`,
      };
    case 'ALREADY_PAID':
      return {
        alertLevel: 'ERROR',
        title: 'Possible duplicate payment',
        // TODO: show the exact date of the previous payment
        // i.e. This invoice looks identical to one you paid on 1 March - same amount, reference, and recipient.
        // https://www.figma.com/design/3b1S7xbXjjWIobGEmEfMdA?node-id=4737-6971&m=dev#1228967346
        description: 'This invoice looks identical to one you already paid - same amount, reference, and recipient.',
      };
    case 'VENDOR_COUNTRY_NOT_SUPPORTED': {
      const description = unsupportedVendorCountry
        ? `We don't support payments to ${unsupportedVendorCountry}. Please upload a new invoice. If you think this is inaccurate, please contact a member of our team.`
        : `We couldn't read the country on this invoice. Please upload a new invoice. If you think this is inaccurate, please contact a member of our team.`;

      return {
        alertLevel: 'ERROR',
        description,
      };
    }
    case 'CURRENCY_NOT_SUPPORTED': {
      const description = unsupportedCurrency
        ? `We don't support payments in ${unsupportedCurrency}. Please upload a new invoice. If you think this is inaccurate, please contact a member of our team.`
        : `We couldn't read the currency on this invoice. Please upload a new invoice. If you think this is inaccurate, please contact a member of our team.`;

      return {
        alertLevel: 'ERROR',
        description,
      };
    }
    case 'GENERIC_ERROR':
      return {
        alertLevel: 'ERROR',
        description: 'An error occurred while processing this invoice. We have been notified. Please try again later.',
      };
    default:
      return exhaustiveCheck(variant);
  }
};

const getAlertIcon = (alertLevel: AlertLevel) => {
  const baseIconProps = { className: 'h-full w-full', color: '#000000' };

  switch (alertLevel) {
    case 'INFO':
      return <Info {...baseIconProps} />;
    case 'WARNING':
      return <AlertTriangle {...baseIconProps} />;
    case 'ERROR':
      return <AlertTriangle {...baseIconProps} color="#FF531D" />;
    default:
      return exhaustiveCheck(alertLevel);
  }
};
